import { catalogEntry } from "@strat/format";
import type { IndicatorDef, IndicatorKind } from "@strat/format";
import type { ThemeMode } from "../prefs/PreferencesProvider";

export interface ChartTheme {
  text: string;
  muted: string;
  grid: string;
  axisLine: string;
  up: string;
  down: string;
  upFill: string;
  downFill: string;
  volume: string;
  accent: string;
  spike: string;
  paper: string;
}

/**
 * Plotly can't read CSS custom properties, so the handful of colors the canvas
 * needs are mirrored here. Keep these in sync with the tokens in styles.css.
 *
 * The accent is the brand gold rather than a third hue: green and red already
 * mean win and loss on these charts, so a benchmark or histogram drawn in any
 * other colour would read as a third outcome.
 */
export const CHART_THEME: Record<ThemeMode, ChartTheme> = {
  dark: {
    text: "#f5f3ea",
    muted: "#958f81",
    grid: "rgba(149, 143, 129, 0.14)",
    axisLine: "rgba(149, 143, 129, 0.3)",
    up: "#2bbf9c",
    down: "#f25d61",
    upFill: "rgba(43, 191, 156, 0.55)",
    downFill: "rgba(242, 93, 97, 0.55)",
    volume: "rgba(149, 143, 129, 0.4)",
    accent: "#e8b400",
    spike: "rgba(232, 180, 0, 0.6)",
    paper: "rgba(0,0,0,0)",
  },
  light: {
    text: "#17140d",
    muted: "#6a6559",
    grid: "rgba(23, 20, 13, 0.09)",
    axisLine: "rgba(23, 20, 13, 0.22)",
    up: "#0d735d",
    down: "#ba3236",
    upFill: "rgba(13, 115, 93, 0.5)",
    downFill: "rgba(186, 50, 54, 0.5)",
    volume: "rgba(106, 101, 89, 0.35)",
    accent: "#895d04",
    spike: "rgba(137, 93, 4, 0.5)",
    paper: "rgba(0,0,0,0)",
  },
};

/**
 * Indicator series colours, led by the brand gold so a single-indicator chart
 * is on-palette by default.
 *
 * Deliberately contains no green and no red. Those two hues already mean "up
 * bar" and "down bar" on the price pane, so an indicator line wearing either
 * stops reading as a line and starts reading as part of the candles — a
 * moving average in win-green is genuinely hard to follow through a rally.
 * Every colour here is a hue the candles never use.
 */
export const SERIES_PALETTE = [
  "#e8b400",
  "#4aa3df",
  "#cf7c1f",
  "#9b7fd4",
  "#00bcd4",
  "#b0a8b9",
  "#6b78d6",
  "#d8a657",
  "#7fb3d5",
  "#a88bd4",
];

const assigned = new Map<string, string>();

export function colorForId(id: string): string {
  const cached = assigned.get(id);
  if (cached) return cached;
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  const color = SERIES_PALETTE[hash % SERIES_PALETTE.length];
  assigned.set(id, color);
  return color;
}

/** Whether an indicator draws anything on a chart — SuperTrend, say, does not. */
export function drawsOnChart(kind: IndicatorKind): boolean {
  const entry = catalogEntry(kind);
  return entry.outputs.some((k) => entry.render?.[k] !== "hidden");
}

/**
 * The colour an indicator's first line is drawn in on the price chart — for
 * a swatch beside its name anywhere outside the chart (a filter, a legend).
 */
export function indicatorColor(def: IndicatorDef): string {
  if (def.color) return def.color;
  const entry = catalogEntry(def.kind);
  const key = entry.outputs.find((k) => entry.render?.[k] !== "hidden") ?? "value";
  if (entry.band && !entry.band.bySign && (key === entry.band.upper || key === entry.band.lower))
    return colorForId(`${def.id}.band`);
  return colorForId(`${def.id}.${key}`);
}
